import { createAsyncThunk } from '@reduxjs/toolkit'
import { getHubs } from 'utils/api'
import {
  getRejectReason,
  putUpdateRefundStatus,
  getRefundComplaintReason,
} from 'utils/apiList/refund'
import { getImageRules, postImage, type PostImageRequestPayload } from 'utils/apiList/images'
import { toastFailed, toastSuccess } from 'utils/toast'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  getRefundList,
  getRefundableProductsV2,
  getRefundDetailV2,
  postCreateRefundV2,
  type PostCreateRefundRequestType,
} from 'features/Payment/services/refund'

import {
  SLICE_NAME,
  setIsLoading,
  setData,
  setPagination,
  setHub,
  setRejectReasonList,
  setIsRejectAction,
  setDetail,
  setForm,
  setIsOpenForm,
  setDataRefundableProducts,
  setComplaintReasonList,
  setRefundImageRules,
  type RefundsRejectReasonType,
  type RefundsDetailType,
} from './slice'

const initialPagination = {
  pageSize: 10,
  currentItem: 0,
  currentPage: 0,
  totalData: 0,
  last: false,
}

type UpdateRefundStatusPayloadType = {
  id: number | string
  status: string
  reject_reason?: string
  refund_method?: string
}

export const getData = createAsyncThunk(
  `${SLICE_NAME}/getData`,
  async (_, { dispatch, rejectWithValue, getState }) => {
    const {
      refunds: {
        params: {
          orderId,
          locationId,
          status,
          createdDateFrom,
          createdDateTo,
          pageSize,
          pageIndex,
          customer_id,
          generatedBy,
        },
      },
    } = getState() as StoreStateType

    try {
      dispatch(setIsLoading(true))
      dispatch(setData([]))
      dispatch(setPagination(initialPagination))

      const { data: dataRes } = await getRefundList({
        params: {
          ...(customer_id && { customer_id }),
          ...(orderId && { order_id: Number(orderId) }),
          ...(locationId && { location_id: Number(locationId) }),
          ...(status && { status }),
          ...(generatedBy && { generated_by: generatedBy }),
          ...((createdDateFrom || Number(createdDateFrom) > 0) && {
            start_time_unix: String(createdDateFrom),
          }),
          ...((createdDateTo || Number(createdDateTo) > 0) && {
            end_time_unix: String(createdDateTo),
          }),
          limit: pageSize,
          page: pageIndex,
        },
      })

      dispatch(setData(dataRes.data.refunds))
      dispatch(
        setPagination({
          pageSize: dataRes.pagination.page_size,
          currentItem: dataRes.pagination.total_elements,
          currentPage: dataRes.pagination.page_index,
          totalData: dataRes.pagination.total_elements,
          last: dataRes.pagination.page_index === dataRes.pagination.total_pages,
        }),
      )
    } catch (error) {
      toastFailed(orderId ? 'Mohon lakukan pencarian berdasarkan Order ID' : 'Gagal Load Data')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const getLocationHub = createAsyncThunk(
  `${SLICE_NAME}/getLocationHub`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getHubs()
      dispatch(setHub(data))
    } catch (error) {
      toastFailed('Gagal Load Data Hub')
      return rejectWithValue(error)
    }
    return undefined
  },
)

export const getRejectionStatus = createAsyncThunk(
  `${SLICE_NAME}/getRejectionStatus`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setIsLoading(true))

      const {
        data: {
          data: { refund_rejection_reason_list },
        },
      } = await getRejectReason()
      dispatch(setRejectReasonList(refund_rejection_reason_list as RefundsRejectReasonType[]))
      dispatch(setIsRejectAction(true))
    } catch (error) {
      toastFailed('Gagal Load Data Reject Reason')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const getDetail = createAsyncThunk(
  `${SLICE_NAME}/getDetail`,
  async (id: number | string, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setIsLoading(true))
      const {
        data: { data },
      } = await getRefundDetailV2(id)
      dispatch(setDetail(data as unknown as RefundsDetailType))
      dispatch(
        setForm({
          id: data.refund_detail.id,
          order_id: data.refund_detail.order_id,
          status: data.refund_detail.status,
          status_name: data.refund_detail.status_fmt,
          refund_method: data.refund_detail.refund_method,
          refund_method_name: data.refund_detail.refund_method_fmt,
          product_list: data.refund_line_items || [],
          notes: data.refund_detail.notes || '-',
          reject_reason: data.refund_detail.reject_reason || '-',
          proofs: data.refund_proofs || [],
          complaint_reason_name: data?.refund_detail?.complaint_reason_fmt || '',
        }),
      )
      dispatch(setIsOpenForm(true))
    } catch (error) {
      dispatch(setIsOpenForm(false))
      toastFailed('Gagal Load Detail Refund')
      return rejectWithValue(error)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const updateRefundStatus = createAsyncThunk(
  `${SLICE_NAME}/putUpdateRefundStatus`,
  async (
    { id, status, reject_reason, refund_method }: UpdateRefundStatusPayloadType,
    { dispatch, getState },
  ) => {
    const {
      refunds: { isRejectAction },
    } = getState() as StoreStateType

    try {
      dispatch(setIsLoading(true))

      const {
        data: {
          data: { message },
        },
      } = await putUpdateRefundStatus(id, {
        status,
        reject_reason,
        refund_method,
      })

      toastSuccess(message)
      dispatch(getData())
    } catch (err) {
      const error = err as {
        response?: { data?: { error?: { message?: string }; error_message?: string } }
      }
      toastFailed(
        error?.response?.data?.error?.message || error?.response?.data?.error_message || '',
      )
    } finally {
      if (isRejectAction) dispatch(setIsRejectAction(false))
      dispatch(setIsOpenForm(false))
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const getRefundableProductsByInvoiceId = createAsyncThunk(
  `${SLICE_NAME}/getRefundableProductsByInvoiceId`,
  async (invoiceId: string | number, { dispatch, rejectWithValue }) => {
    dispatch(setDataRefundableProducts(null))

    try {
      dispatch(setIsLoading(true))
      const response = await getRefundableProductsV2({
        invoice: invoiceId,
      })

      const { available_refund_line_items, refund_methods_approval, ...rest } = response.data.data

      dispatch(
        setDataRefundableProducts({
          ...rest,
          refundable_products: available_refund_line_items,
          refund_method_approval: refund_methods_approval,
        }),
      )
    } catch (err) {
      const error = err as { response?: { data?: { error_message?: string } } }
      const errMessage = error?.response?.data?.error_message
      callErrorMsg(err, errMessage)

      return rejectWithValue(err)
    } finally {
      dispatch(setIsLoading(false))
    }
    return undefined
  },
)

export const uploadImageProof = createAsyncThunk(
  `${SLICE_NAME}/uploadImageProof`,
  async (data: PostImageRequestPayload, { rejectWithValue }) => {
    try {
      const response = await postImage('refund-images', data)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(err)
    }
  },
)

export const createRefund = createAsyncThunk(
  `${SLICE_NAME}/createRefund`,
  async (payload: PostCreateRefundRequestType, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setIsLoading(true))
      const result = await postCreateRefundV2(payload)
      toastSuccess('Pengembalian pesanan berhasil dibuat')

      dispatch(setIsLoading(false))

      return result.data
    } catch (err) {
      dispatch(setIsLoading(false))

      const error = err as { response?: { data?: { error_message?: string } } }
      const errMessage = error?.response?.data?.error_message
      callErrorMsg(err, errMessage)

      return rejectWithValue(err)
    }
  },
)

export const getComplaintReasons = createAsyncThunk(
  `${SLICE_NAME}/getComplaintReasons`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await getRefundComplaintReason()

      const data = result?.data?.data?.refund_complaint_reason_list || []

      const transformedData = data.map(
        (item: {
          reason: string
          refund_complaint_reason_id: number | string
          is_text_mandatory: boolean
        }) => ({
          name: item.reason,
          value: item.refund_complaint_reason_id,
          isTextMandatory: item.is_text_mandatory,
        }),
      )

      dispatch(setComplaintReasonList(transformedData))
    } catch (err) {
      callErrorMsg(err, 'Gagal mendapatkan daftar alasan refund')

      return rejectWithValue(err)
    }
    return undefined
  },
)

export const getRefundImageRules = createAsyncThunk(
  `${SLICE_NAME}/getRefundImageRules`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await getImageRules('refund-images')

      const data = result?.data?.data || null

      dispatch(setRefundImageRules(data))

      return data
    } catch (err) {
      callErrorMsg(err, 'Gagal mendapatkan refund image rules')

      return rejectWithValue(err)
    }
  },
)
